// src/hooks/usePackages.ts

import { useState, useCallback } from 'react';
import { packageService } from '../services/package.service';
import {
  PackageResponse, CreatePackageCommand, PackageStatus,
  AddLocationCommand, PackageSearchCriteria, PagedResponse,
} from '../types/package.types';

interface PackagesState {
  packages: PagedResponse<PackageResponse> | null;
  selected: PackageResponse | null;
  isLoading: boolean;
  error: string | null;
}

export const usePackages = () => {
  const [state, setState] = useState<PackagesState>({
    packages: null, selected: null, isLoading: false, error: null,
  });

  const setLoading = () => setState(p => ({ ...p, isLoading: true, error: null }));
  const setError   = (error: string) => setState(p => ({ ...p, isLoading: false, error }));

  const search = useCallback(async (criteria: PackageSearchCriteria = {}, page = 0) => {
    setLoading();
    try {
      const data = await packageService.search(criteria, page);
      setState(p => ({ ...p, packages: data, isLoading: false }));
    } catch (e) { setError((e as Error).message); }
  }, []);

  const getById = useCallback(async (id: string) => {
    setLoading();
    try {
      const data = await packageService.getById(id);
      setState(p => ({ ...p, selected: data, isLoading: false }));
      return data;
    } catch (e) { setError((e as Error).message); return null; }
  }, []);

  const create = useCallback(async (command: CreatePackageCommand): Promise<PackageResponse | null> => {
    setLoading();
    try {
      const data = await packageService.create(command);
      setState(p => ({ ...p, isLoading: false }));
      return data;
    } catch (e) { setError((e as Error).message); return null; }
  }, []);

  const updateStatus = useCallback(async (id: string, status: PackageStatus): Promise<boolean> => {
    setLoading();
    try {
      const updated = await packageService.updateStatus(id, status);
      setState(p => ({
        ...p,
        isLoading: false,
        selected: p.selected?.id === id ? updated : p.selected,
        packages: p.packages ? {
          ...p.packages,
          content: p.packages.content.map(pkg => pkg.id === id ? updated : pkg),
        } : null,
      }));
      return true;
    } catch (e) { setError((e as Error).message); return false; }
  }, []);

  const addLocation = useCallback(async (id: string, cmd: AddLocationCommand): Promise<boolean> => {
    setLoading();
    try {
      const updated = await packageService.addLocation(id, cmd);
      setState(p => ({ ...p, isLoading: false, selected: updated }));
      return true;
    } catch (e) { setError((e as Error).message); return false; }
  }, []);

  const deletePackage = useCallback(async (id: string): Promise<boolean> => {
    setLoading();
    try {
      await packageService.deletePackage(id);
      setState(p => ({
        ...p,
        isLoading: false,
        packages: p.packages ? {
          ...p.packages,
          content: p.packages.content.filter(pkg => pkg.id !== id),
          totalElements: p.packages.totalElements - 1,
        } : null,
      }));
      return true;
    } catch (e) { setError((e as Error).message); return false; }
  }, []);

  const clearError   = () => setState(p => ({ ...p, error: null }));
  const clearSelected = () => setState(p => ({ ...p, selected: null }));

  return { ...state, search, getById, create, updateStatus, addLocation, deletePackage, clearError, clearSelected };
};
